import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Confirm,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Loader,
  Message,
  Segment,
  Table,
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import { useToast } from '../../hooks/useToast';
import BaseModal, { BaseModalAction } from '../../components/shared/BaseModal';
import {
  authorizedDevicesService,
  AuthorizedDeviceActiveFilter,
  AuthorizedDeviceViewModel,
  DeviceRestrictionMode,
} from '../../services/authorizedDevices/authorizedDevicesService';
import { isBrowserAuthorizedFromList } from '../../services/authorizedDevices/authorizedDevicesUtils';
import { getStoredDeviceToken } from '../../utils/deviceTokenStorage';

const MODE_OPTIONS: { key: DeviceRestrictionMode; value: DeviceRestrictionMode; text: string }[] = [
  { key: 'off', value: 'off', text: 'Off' },
  { key: 'auditOnly', value: 'auditOnly', text: 'Só registrar' },
  { key: 'enforce', value: 'enforce', text: 'Bloquear' },
];

const STATUS_FILTER_OPTIONS = [
  { key: 'all', value: 'all', text: 'Todos' },
  { key: 'active', value: 'active', text: 'Ativos' },
  { key: 'revoked', value: 'revoked', text: 'Revogados' },
];

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR');
}

const AuthorizedDevicesPage: React.FC = () => {
  const { showSuccess, showError } = useToast();

  const [mode, setMode] = useState<DeviceRestrictionMode>('off');
  const [modeLoading, setModeLoading] = useState(true);
  const [modeSaving, setModeSaving] = useState(false);
  const [confirmEnforceOpen, setConfirmEnforceOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<DeviceRestrictionMode | null>(null);

  const [devices, setDevices] = useState<AuthorizedDeviceViewModel[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [unitFilter, setUnitFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<AuthorizedDeviceActiveFilter>('all');

  const [authorizeOpen, setAuthorizeOpen] = useState(false);
  const [authorizeName, setAuthorizeName] = useState('');
  const [authorizeUnit, setAuthorizeUnit] = useState('');
  const [authorizing, setAuthorizing] = useState(false);

  const [renameDevice, setRenameDevice] = useState<AuthorizedDeviceViewModel | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  const [actionDeviceId, setActionDeviceId] = useState<string | null>(null);

  const browserHasToken = Boolean(getStoredDeviceToken());
  const browserAuthorized = useMemo(() => isBrowserAuthorizedFromList(devices), [devices]);

  const unitOptions = useMemo(() => {
    const units = new Set<string>();
    devices.forEach((d) => {
      if (d.unit?.trim()) units.add(d.unit.trim());
    });
    return [
      { key: 'all', value: '', text: 'Todas as unidades' },
      ...Array.from(units)
        .sort((a, b) => a.localeCompare(b, 'pt-BR'))
        .map((u) => ({ key: u, value: u, text: u })),
    ];
  }, [devices]);

  const loadMode = useCallback(async () => {
    try {
      setModeLoading(true);
      const settings = await authorizedDevicesService.getRestrictionSettings();
      setMode(settings.mode);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao carregar modo de restrição');
    } finally {
      setModeLoading(false);
    }
  }, [showError]);

  const loadDevices = useCallback(async () => {
    try {
      setDevicesLoading(true);
      const list = await authorizedDevicesService.listDevices({
        unit: unitFilter || undefined,
        active: activeFilter,
      });
      setDevices(list);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao carregar computadores autorizados');
    } finally {
      setDevicesLoading(false);
    }
  }, [showError, activeFilter, unitFilter]);

  useEffect(() => {
    void loadMode();
  }, [loadMode]);

  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);

  const persistMode = async (next: DeviceRestrictionMode) => {
    try {
      setModeSaving(true);
      const updated = await authorizedDevicesService.updateRestrictionSettings({ mode: next });
      setMode(updated.mode);
      showSuccess('Modo de restrição atualizado');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao salvar modo');
    } finally {
      setModeSaving(false);
      setConfirmEnforceOpen(false);
      setPendingMode(null);
    }
  };

  const handleModeChange = (_: React.SyntheticEvent, data: { value?: string | number | boolean | (string | number | boolean)[] }) => {
    const raw = data.value;
    const next = (Array.isArray(raw) ? raw[0] : raw) as DeviceRestrictionMode;
    if (next === mode) return;
    if (next === 'enforce') {
      setPendingMode(next);
      setConfirmEnforceOpen(true);
      return;
    }
    void persistMode(next);
  };

  const handleAuthorize = async () => {
    const name = authorizeName.trim();
    const unit = authorizeUnit.trim();
    if (!name || !unit) {
      showError('Informe nome e unidade do computador');
      return;
    }
    try {
      setAuthorizing(true);
      await authorizedDevicesService.authorizeCurrentDevice({ name, unit });
      showSuccess('Computador autorizado neste navegador');
      setAuthorizeOpen(false);
      setAuthorizeName('');
      setAuthorizeUnit('');
      await loadDevices();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao autorizar computador');
    } finally {
      setAuthorizing(false);
    }
  };

  const handleRename = async () => {
    if (!renameDevice) return;
    const name = renameValue.trim();
    if (!name) {
      showError('Informe o nome');
      return;
    }
    try {
      setRenaming(true);
      await authorizedDevicesService.renameDevice(renameDevice.id, { name });
      showSuccess('Nome atualizado');
      setRenameDevice(null);
      await loadDevices();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao renomear');
    } finally {
      setRenaming(false);
    }
  };

  const handleRevoke = async (device: AuthorizedDeviceViewModel) => {
    try {
      setActionDeviceId(device.id);
      await authorizedDevicesService.revokeDevice(device.id);
      showSuccess('Computador revogado');
      await loadDevices();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao revogar');
    } finally {
      setActionDeviceId(null);
    }
  };

  const handleReactivate = async (device: AuthorizedDeviceViewModel) => {
    try {
      setActionDeviceId(device.id);
      await authorizedDevicesService.reactivateDevice(device.id);
      showSuccess('Computador reativado');
      await loadDevices();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao reativar');
    } finally {
      setActionDeviceId(null);
    }
  };

  const authorizeActions: BaseModalAction[] = [
    { key: 'cancel', content: 'Cancelar', icon: 'cancel', onClick: () => setAuthorizeOpen(false) },
    {
      key: 'save',
      content: 'Autorizar',
      icon: 'check',
      primary: true,
      loading: authorizing,
      onClick: () => void handleAuthorize(),
    },
  ];

  const renameActions: BaseModalAction[] = [
    {
      key: 'cancel',
      content: 'Cancelar',
      icon: 'cancel',
      onClick: () => setRenameDevice(null),
    },
    {
      key: 'save',
      content: 'Salvar',
      icon: 'save',
      primary: true,
      loading: renaming,
      onClick: () => void handleRename(),
    },
  ];

  return (
    <GlassPageWrapper>
      <Header as="h1" style={{ color: '#fff', marginBottom: '1rem' }}>
        <Icon name="desktop" />
        Computadores autorizados
      </Header>
      <Button as={Link} to="/authorized-devices/exemptions" basic inverted size="small" style={{ marginBottom: '1rem' }}>
        <Icon name="unlock alternate" />
        Liberação por usuário
      </Button>

      <Segment inverted style={{ background: 'rgba(255,255,255,0.06)', marginBottom: '1.5rem' }}>
        <Header as="h3" inverted>
          Modo de restrição
        </Header>
        {modeLoading ? (
          <Loader active inline="centered" />
        ) : (
          <Form>
            <Form.Field>
              <label style={{ color: '#ddd' }}>Comportamento no login</label>
              <Dropdown
                selection
                options={MODE_OPTIONS}
                value={mode}
                onChange={handleModeChange}
                disabled={modeSaving}
              />
            </Form.Field>
          </Form>
        )}
      </Segment>

      <Segment inverted style={{ background: 'rgba(255,255,255,0.06)', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <Button
            primary
            icon="plus"
            content="Autorizar este computador"
            onClick={() => setAuthorizeOpen(true)}
          />
          {browserAuthorized ? (
            <Label color="green" size="large">
              <Icon name="check circle" />
              Este navegador está autorizado
            </Label>
          ) : browserHasToken ? (
            <Label color="yellow" size="large">
              <Icon name="warning sign" />
              Token local sem correspondência ativa
            </Label>
          ) : (
            <Label size="large">
              <Icon name="info circle" />
              Este navegador ainda não possui autorização
            </Label>
          )}
        </div>
      </Segment>

      <Segment inverted style={{ background: 'rgba(255,255,255,0.06)' }}>
        <Form style={{ marginBottom: '1rem' }}>
          <Form.Group widths="equal">
            <Form.Field>
              <label style={{ color: '#ddd' }}>Unidade</label>
              <Dropdown
                selection
                clearable
                search
                options={unitOptions}
                value={unitFilter}
                onChange={(_, { value }) => setUnitFilter(String(value ?? ''))}
                placeholder="Filtrar por unidade"
              />
            </Form.Field>
            <Form.Field>
              <label style={{ color: '#ddd' }}>Status</label>
              <Dropdown
                selection
                options={STATUS_FILTER_OPTIONS}
                value={activeFilter}
                onChange={(_, { value }) =>
                  setActiveFilter((value as AuthorizedDeviceActiveFilter) || 'all')
                }
              />
            </Form.Field>
          </Form.Group>
        </Form>

        {devicesLoading ? (
          <Loader active inline="centered" />
        ) : devices.length === 0 ? (
          <Message info>Nenhum computador encontrado para os filtros selecionados.</Message>
        ) : (
          <Table celled striped inverted compact="very">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Nome</Table.HeaderCell>
                <Table.HeaderCell>Unidade</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Último uso</Table.HeaderCell>
                <Table.HeaderCell>Último IP</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {devices.map((device) => (
                <Table.Row key={device.id} positive={device.isCurrentDevice === true}>
                  <Table.Cell>
                    {device.name}
                    {device.isCurrentDevice ? (
                      <Label size="mini" color="blue" style={{ marginLeft: 8 }}>
                        Este navegador
                      </Label>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>{device.unit}</Table.Cell>
                  <Table.Cell>
                    {device.isActive ? (
                      <Label color="green" size="small">
                        Ativo
                      </Label>
                    ) : (
                      <Label color="red" size="small">
                        Revogado
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>{formatDateTime(device.lastUsedAt)}</Table.Cell>
                  <Table.Cell>{device.lastIp || '—'}</Table.Cell>
                  <Table.Cell textAlign="center">
                    <Button
                      size="mini"
                      icon="edit"
                      title="Renomear"
                      onClick={() => {
                        setRenameDevice(device);
                        setRenameValue(device.name);
                      }}
                    />
                    {device.isActive ? (
                      <Button
                        size="mini"
                        color="red"
                        icon="ban"
                        title="Revogar"
                        loading={actionDeviceId === device.id}
                        onClick={() => void handleRevoke(device)}
                      />
                    ) : (
                      <Button
                        size="mini"
                        color="green"
                        icon="undo"
                        title="Reativar"
                        loading={actionDeviceId === device.id}
                        onClick={() => void handleReactivate(device)}
                      />
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      <BaseModal
        isOpen={authorizeOpen}
        onClose={() => setAuthorizeOpen(false)}
        title="Autorizar este computador"
        icon="desktop"
        actions={authorizeActions}
      >
        <Message info>
          O token será salvo neste navegador e enviado nos logins. Ele permanece após logout.
        </Message>
        <Form>
          <Form.Input
            label="Nome do computador"
            value={authorizeName}
            onChange={(_, { value }) => setAuthorizeName(String(value))}
            placeholder="Ex.: PC Recepção 01"
            required
          />
          <Form.Input
            label="Unidade"
            value={authorizeUnit}
            onChange={(_, { value }) => setAuthorizeUnit(String(value))}
            placeholder="Ex.: Matriz, Filial SP..."
            required
          />
        </Form>
      </BaseModal>

      <BaseModal
        isOpen={!!renameDevice}
        onClose={() => setRenameDevice(null)}
        title="Renomear computador"
        icon="edit"
        actions={renameActions}
      >
        <Input
          fluid
          value={renameValue}
          onChange={(_, { value }) => setRenameValue(String(value))}
          placeholder="Novo nome"
        />
      </BaseModal>

      <Confirm
        open={confirmEnforceOpen}
        header="Ativar bloqueio de computadores?"
        content="Usuários só poderão entrar em computadores previamente autorizados (exceto quem tiver liberação). Deseja continuar?"
        confirmButton="Sim, bloquear"
        cancelButton="Cancelar"
        onCancel={() => {
          setConfirmEnforceOpen(false);
          setPendingMode(null);
        }}
        onConfirm={() => {
          if (pendingMode) void persistMode(pendingMode);
        }}
      />
    </GlassPageWrapper>
  );
};

export default AuthorizedDevicesPage;
